<template>
    <div class="page-wrap">
        <!-- Title and tabs stick together (same pattern as Scoreboard.vue) -->
        <div class="page-bar-sticky">
            <div class="page-bar">
                <div>
                    <div class="page-bar-sub">AI</div>
                    <div class="page-bar-main">The Forge</div>
                </div>
            </div>
            <div class="page-bar page-bar-toggles-wrap">
                <div class="page-bar-toggles forge-tabs" role="tablist">
                    <div role="tab" :aria-selected="tab == 'make'" :class="tab == 'make' ? 'page-bar-toggle-s' : ''" @click="setTab('make')">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M10 2l1.8 5.2L17 9l-5.2 1.8L10 16l-1.8-5.2L3 9l5.2-1.8zM18 13l.9 2.6 2.6.9-2.6.9L18 20l-.9-2.6-2.6-.9 2.6-.9zM18 2l.6 1.9 1.9.6-1.9.6L18 7l-.6-1.9-1.9-.6 1.9-.6z"/></svg><em class="ft-label">Make<i class="ft-long"> something</i></em>
                    </div>
                    <div role="tab" :aria-selected="tab == 'captures'" :class="tab == 'captures' ? 'page-bar-toggle-s' : ''" @click="setTab('captures')">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="12" rx="2"/><path d="M16 10l6-3v10l-6-3z"/></svg><em class="ft-label"><i class="ft-long">My captures</i><i class="ft-short">Captures</i></em><span v-if="needsYou">{{ needsYou }}</span>
                    </div>
                    <div role="tab" :aria-selected="tab == 'health'" :class="tab == 'health' ? 'page-bar-toggle-s' : ''" @click="setTab('health')">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4M6 10h3l2-3 2 6 2-3h3"/></svg><em class="ft-label">Health</em><span v-if="suggestions.length + teamGroups.length">{{ suggestions.length + teamGroups.length }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
        <!-- MAKE, front screen (Keelan picked mockup C "Forge band", 3 Nov 2026): a dark band carries the start box,
             full width under the tabs; the tools sit below as cards in four groups. -->
        <section v-show="tab == 'make' && !tool" class="fb-band" aria-labelledby="fs-start-title">
            <div class="fb-inner">
                <svg class="fb-flame" aria-hidden="true" viewBox="0 0 24 24"><path d="M12 2c1 3.5 5 5.5 5 10.5A5 5 0 0 1 7 12.5c0-2 1-3.5 2.2-4.6.2 1.6 1 2.6 2 3C11 8 11 5 12 2z"/></svg>
                <h2 id="fs-start-title">What do you want to make?</h2>
                <p>Describe it in a sentence and the Forge opens the right tool with your words filled in.</p>
                <div class="fb-bar">
                    <textarea id="fs-start-text" ref="startBox" v-model="startText" rows="1" maxlength="8000" aria-label="What do you want to make?" placeholder="Rocks for the sales team this quarter" @input="growStart" @keydown.enter.exact.prevent="routeStart()"></textarea>
                    <button type="button" class="fb-go" :disabled="routing || !startText.trim()" @click="routeStart()">{{ routing ? 'Finding it…' : 'Start' }}</button>
                </div>
                <div v-if="startError" class="fb-error" role="alert">{{ startError }}</div>
                <div v-if="examples.length" class="fb-examples">
                    <button v-for="ex in examples" :key="ex.text" type="button" class="fb-ex" :disabled="routing" @click="tryExample(ex.text)">{{ ex.text }}</button>
                </div>
            </div>
        </section>
        <div class="ai-hub" :class="{ 'ai-hub-wide': tab == 'make' && !tool }">
            <!-- MAKE: one start box and one menu. Picking a tool (or Start) swaps the menu for that tool, with
                 "All tools" to come back, so every tool shows once and there is only ever one box to type in. -->
            <div v-show="tab == 'make'">
                <a v-if="!tool && needsYou" href="#" class="fb-waiting" @click.prevent="setTab('captures')">
                    <span class="fb-count">{{ needsYou }}</span>
                    <span class="fb-waiting-text">{{ needsYou }} capture{{ needsYou == 1 ? ' is' : 's are' }} waiting for you to review.</span>
                    <span class="fb-waiting-go">Open My captures</span>
                </a>
                <div v-show="!tool" class="fb-groups">
                    <div v-for="g in menuGroups" :key="g.label" class="fb-group">
                        <h3>{{ g.label }}</h3>
                        <button v-for="t in g.tools" :key="t.id" type="button" class="fb-tile" :class="{ 'fb-tile-dark': g.label == 'Record' }" @click="openTool(t)">
                            <span class="fb-ic"><svg aria-hidden="true" viewBox="0 0 24 24"><path :d="toolIcon(t.id)"/></svg></span>
                            <span class="fb-tn">{{ t.label }}</span>
                            <span class="fb-td">{{ toolBlurb(t) }}</span>
                        </button>
                    </div>
                </div>

                <div v-if="tool" ref="toolBar" class="fs-toolbar">
                    <button type="button" class="fs-back" @click="backToMenu()"><span aria-hidden="true">&larr;</span> All tools</button>
                    <span v-if="startNote" class="fs-note" aria-live="polite">{{ startNote }}</span>
                </div>
                <ai-create-node v-if="tool && toolGroup == 'playbook'" ref="createNode" :inline="true" :key="createKey" :node-type="createType" :initial-note="createNote" :initial-method="createMethod" @close="onCreateClose" />
                <forge-team v-show="tool && toolGroup == 'team'" ref="forgeTeam" :single="true" />
            </div>

            <!-- MY CAPTURES: recordings, uploads, hand-offs and meeting notes (was a list under the tools) -->
            <div v-show="tab == 'captures'" id="captures">
                <!-- Notetaker: Outlook-connected meeting bot (ForgeNotetaker.vue, routes/ainotetaker.ts) -->
                <forge-notetaker class="fh-notetaker" />

                <div v-if="videoJobs.length" class="ai-drafts">
                    <div class="ai-drafts-title">Recordings, uploads and meeting notes</div>
                    <div v-for="job in videoJobs" :key="job.id" :data-job-id="job.id" class="ai-drafts-row">
                        <span class="ai-drafts-chip" :class="'ai-drafts-' + job.status">
                            {{ job.status == 'processing' ? (job.stage || 'Processing') + '...' : job.status == 'complete' ? 'Ready to review' : job.status == 'recording' ? 'Recording — not yet submitted' : job.status == 'handoff' ? (job.stage ? job.stage + '...' : job.handoffToMe ? 'Sent to you — to build' : 'Waiting for ' + (job.handoffTo || 'the builder')) : job.status == 'discarded' ? 'Recording discarded' : job.status == 'consumed' ? (job.nodeType == 'meeting' ? (job.hasMeetingNotes ? 'Meeting notes saved' : 'Recording discarded') : job.targetSlug ? 'Update applied' : jobTypeName(job.nodeType) + ' created') : 'Failed' }}
                        </span>
                        <span class="ai-drafts-name">
                            {{ job.draftName || (job.nodeType == 'meeting' ? 'Meeting notes' : job.sourceLabel == 'photos' ? 'Photo capture' : job.sourceLabel == 'documents' ? 'Document upload' : job.sourceLabel == 'files' ? 'Photos + documents' : job.sourceLabel == 'recording' ? 'Screen recording' : 'Uploaded video') }}
                            <span v-if="job.targetSlug" class="ai-drafts-sub">{{ job.mode == 'extend' ? 'adds to an existing ' + (job.nodeType == 'timeline' ? 'value engine' : job.nodeType || 'process') : 'updates an existing process' }}</span>
                            <span v-else-if="job.handoffFrom" class="ai-drafts-sub">{{ job.handoffToMe ? 'from ' + job.handoffFrom : 'sent to ' + job.handoffTo }}</span>
                        </span>
                        <span class="ai-drafts-date">{{ new Date(job.created).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) }}</span>
                        <router-link v-if="job.nodeType == 'meeting' || ['complete', 'processing', 'handoff'].includes(job.status)" :to="'/ai-review/' + job.id" class="ai-drafts-link">{{ job.status == 'complete' ? 'Review' : job.status == 'handoff' ? 'Open' : 'View' }}</router-link>
                        <router-link v-else-if="job.status == 'consumed' && job.createdSlug" :to="'/edit-process/' + job.createdSlug" class="ai-drafts-link">Open</router-link>
                        <span v-else class="ai-drafts-err" :title="job.error">&nbsp;</span>
                        <router-link v-if="job.status == 'consumed' && job.videoSlug" :to="'/playbook/' + job.videoSlug" class="ai-drafts-link">Video</router-link>
                    </div>
                </div>

                <div v-if="capturesLoaded && !videoJobs.length" class="ai-empty ai-empty-short">
                    <p>Nothing here yet. Screen recordings, uploads and meeting recordings show up here while the Forge works on them, and stay so you can come back to them.</p>
                </div>
                <!-- My voice: the person's own opt-in voice profile (AIMeetingReview.vue sets it up) -->
                <section v-if="myVoice?.available && (myVoice.enrolled || videoJobs.some(j => j.nodeType == 'meeting'))" class="ai-voice" aria-labelledby="ai-voice-title">
                    <div id="ai-voice-title" class="ai-drafts-title">My voice</div>
                    <p v-if="myVoice.enrolled">Your voice is remembered, so meeting recordings put your name on it automatically. Saved {{ new Date(myVoice.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) }} from {{ Math.round(myVoice.sampleSeconds) }} seconds of speech. It is a voiceprint (numbers), not audio, and only you can change or remove it.
                        <button type="button" class="ai-voice-forget" :disabled="voiceBusy" @click="forgetVoice">Forget my voice</button></p>
                    <p v-else>Not saved. To have meeting recordings name you automatically, open a processed meeting, go to Transcript &amp; speakers, name your voice and tick “Remember my voice”.</p>
                    <p v-if="voiceMessage" role="status" class="ai-voice-note">{{ voiceMessage }}</p>
                </section>
            </div>

            <!-- HEALTH: Company (people, teams, the chart; Team Hub admins) and Processes in one tab -->
            <div v-show="tab == 'health'">
                <div v-if="isTeamHubAdmin" class="fh-tiles" role="tablist" aria-label="Health">
                    <button type="button" role="tab" class="fh-tile" :class="{ 'fh-tile-on': healthView == 'company' }" :aria-selected="healthView == 'company'" @click="setHealthView('company')">
                        <span class="fh-mini" :class="companySummary && companySummary.score != null ? 'fh-ring-' + healthClass(companySummary.score) : 'fh-mini-none'">
                            <svg v-if="companySummary && companySummary.score != null" viewBox="0 0 72 72" aria-hidden="true"><circle cx="36" cy="36" r="31" class="fh-ring-track" /><circle cx="36" cy="36" r="31" class="fh-ring-fill" :stroke-dasharray="ringDash(companySummary.score)" /></svg>
                            <b>{{ companySummary && companySummary.score != null ? companySummary.score : '–' }}</b>
                        </span>
                        <span class="fh-tile-text"><strong>Company</strong><em>{{ companySummary ? companySummary.gaps + ' gap' + (companySummary.gaps == 1 ? '' : 's') + ' · ' + companySummary.checks + ' checks' : 'People, teams and the chart' }}</em></span>
                    </button>
                    <button type="button" role="tab" class="fh-tile" :class="{ 'fh-tile-on': healthView == 'processes' }" :aria-selected="healthView == 'processes'" @click="setHealthView('processes')">
                        <span class="fh-mini" :class="health ? 'fh-ring-' + healthClass(health.current) : 'fh-mini-none'">
                            <svg v-if="health" viewBox="0 0 72 72" aria-hidden="true"><circle cx="36" cy="36" r="31" class="fh-ring-track" /><circle cx="36" cy="36" r="31" class="fh-ring-fill" :stroke-dasharray="ringDash(health.current)" /></svg>
                            <b>{{ health ? health.current : '–' }}</b>
                        </span>
                        <span class="fh-tile-text"><strong>Processes</strong><em>{{ suggestions.length }} flagged<template v-if="teamGroups.length"> · {{ teamGroups.length }} from your team</template><template v-if="health && health.projected > health.current"> · &uarr; {{ health.projected }} after fixes</template></em></span>
                    </button>
                </div>

                <company-health v-if="isTeamHubAdmin && companyMounted" v-show="healthView == 'company'" :bare="true" @summary="s => companySummary = s" />

                <div v-show="healthView == 'processes' || !isTeamHubAdmin">
                <section class="fh-card" :class="{ 'fh-card-slim': isTeamHubAdmin }">
                    <template v-if="!isTeamHubAdmin">
                    <div v-if="health" class="fh-ring" :class="'fh-ring-' + healthClass(health.current)">
                        <svg viewBox="0 0 72 72" aria-hidden="true"><circle cx="36" cy="36" r="31" class="fh-ring-track" /><circle cx="36" cy="36" r="31" class="fh-ring-fill" :stroke-dasharray="ringDash(health.current)" /></svg>
                        <span>{{ health.current }}</span>
                    </div>
                    <div v-else class="fh-ring fh-ring-none"><span>&ndash;</span></div>
                    </template>
                    <div class="fh-meta">
                        <div v-if="!isTeamHubAdmin" class="fh-label">Process health</div>
                        <template v-if="health">
                            <div v-if="!isTeamHubAdmin" class="fh-score">
                                <strong>{{ health.current }}</strong>
                                <span v-if="health.projected > health.current" class="fh-proj">&uarr; {{ health.projected }} after fixes</span>
                            </div>
                            <div class="fh-sub">{{ health.count }} process{{ health.count == 1 ? '' : 'es' }} &middot; {{ suggestions.length }} flagged<template v-if="lastChecked"> &middot; last checked {{ lastChecked }}</template></div>
                        </template>
                        <div v-else class="fh-sub">Not checked yet. Check processes to score every live process.</div>
                    </div>
                    <div class="fh-actions">
                        <router-link v-if="isTrainer" to="/ai-training" class="fh-rules" title="Set the rules the Forge follows">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>
                            Company rules <span>{{ trainingRules.length }}</span>
                        </router-link>
                        <span v-else-if="trainingRules.length" class="fh-rules fh-rules-static" :title="trainingRules.join('\n')">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>
                            Company rules <span>{{ trainingRules.length }}</span>
                        </span>
                        <!-- Super admins only (routes/competitive.ts /access); it used to be reachable only via Company rules -->
                        <router-link v-if="competitiveAllowed" to="/competitive-analysis" class="fh-rules" title="IronBook's weekly market research">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18M7 15l4-4 3 3 5-6"/></svg>
                            Competitive analysis
                        </router-link>
                        <!-- Same audience (routes/usage.ts /access): usage across every company -->
                        <router-link v-if="usageAllowed" to="/usage" class="fh-rules" title="How many people use IronBook, per company">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            IronBook usage
                        </router-link>
                        <div v-if="!confirmData" class="ai-btn" :class="running ? 'disable-button' : ''" @click="askConfirm()">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"/></svg>
                            {{ running ? 'Checking...' : (health || suggestions.length ? 'Check again' : 'Check Processes') }}
                        </div>
                        <div v-else class="ai-confirm">
                            <div class="ai-confirm-text">
                                Check <strong>{{ confirmData.count }}</strong> live process{{ confirmData.count == 1 ? '' : 'es' }}?
                                <span class="ai-confirm-est">est. ~{{ confirmData.estMinutes }} min</span>
                            </div>
                            <div class="ai-btn" @click="startAnalysis()">Start</div>
                            <div class="ai-btn ai-btn-ghost" @click="confirmData = null">Cancel</div>
                        </div>
                    </div>
                    <div v-if="usage && isTrainer" class="ai-usage-note" title="Estimated Anthropic API cost this month at list pricing">
                        AI use this month: {{ usage.calls }} call{{ usage.calls == 1 ? '' : 's' }} &middot; about ${{ usage.estCostUSD }}
                    </div>
                </section>
                <p class="fh-intro">The Forge reads every live process, scores it, and flags gaps like missing owners, vague steps and missing safety checks. Each fix is saved as a draft. Nothing goes live without someone signing off.</p>

                <!-- FROM YOUR TEAM: "Suggest an Improvement" from process pages; the Forge drafts the change (ForgeSuggestionDraft) -->
                <section v-if="teamGroups.length || teamError" class="ft-team" aria-labelledby="ft-team-title">
                    <div class="ft-team-h">
                        <h3 id="ft-team-title">From your team <span class="ft-team-count">{{ teamSuggestions.length }}</span></h3>
                        <router-link to="/review?type=Process%20Suggestions" class="ft-team-all">All suggestions in Review</router-link>
                    </div>
                    <div v-if="teamError" class="ai-run-error">{{ teamError }}</div>
                    <div v-for="g in shownTeamGroups" :key="g.key" class="ft-team-row">
                        <div class="ft-team-main">
                            <div class="ft-team-node">
                                <router-link v-if="g.first.Slug" :to="'/playbook/' + g.first.Slug" target="_blank">{{ g.first.NodeName || 'A playbook page' }}</router-link>
                                <span v-else>{{ g.first.NodeName || 'No page given' }}</span>
                                <span v-if="g.rows.length > 1" class="ft-team-dup" :title="g.rows.map(r => r.User).join(', ')">{{ g.rows.length }} people suggested this</span>
                                <span v-if="g.first.ForgeStatus == 'drafted'" class="ai-draft-flag">Forge draft ready</span>
                                <span v-else-if="g.first.DraftPending" class="ai-draft-flag">draft pending</span>
                            </div>
                            <p class="ft-team-text">{{ g.first.Text }}</p>
                            <div class="ft-team-by">{{ g.rows.map(r => r.User).filter((u, i, a) => a.indexOf(u) == i).join(', ') }} · {{ shortDate(g.first.Date) }}</div>
                        </div>
                        <div class="ft-team-acts">
                            <div v-if="g.first.CanDraft" class="ai-btn" @click="openTeamSuggestion(g)">{{ g.first.ForgeStatus == 'drafted' ? 'Open the draft' : 'Draft with the Forge' }}</div>
                            <span v-else class="ft-team-na">Not a process: edit it by hand</span>
                            <a class="fh-dismiss" @click.prevent="dismissTeamGroup(g)">Dismiss</a>
                        </div>
                    </div>
                    <a v-if="teamGroups.length > teamShow" class="ft-team-more" @click.prevent="teamShow += 10">Show {{ Math.min(10, teamGroups.length - teamShow) }} more</a>
                </section>
                <forge-suggestion-draft v-if="teamOpen" :suggestion="teamOpen.first" @close="teamOpen = null" @drafted="id => markTeam(id, 'drafted')"
                    @saved="e => teamSaved(e)" @dismissed="() => { const g = teamOpen; teamOpen = null; dismissTeamGroup(g); }" />

                <div v-if="running && run" class="ai-progress">
                    <div class="ai-progress-label">
                        Checking processes... {{ run.Completed }} of {{ run.Total }} complete
                    </div>
                    <div class="ai-progress-track">
                        <div class="ai-progress-bar" :style="{ width: progressPercent + '%' }"></div>
                    </div>
                </div>
                <div v-if="run && run.Status == 'error' && run.Error" class="ai-run-error">{{ run.Error }}</div>

                <div v-if="!running && !suggestions.length && loaded" class="ai-empty" :class="{ 'ai-empty-short': teamGroups.length }">
                    <p v-if="health">No flagged processes from the last check.</p>
                    <p v-else>No processes checked yet. Check processes to have the Forge review every live process.</p>
                </div>

                <div v-if="suggestions.length" class="fh-tools">
                    <div class="ai-search">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                        <input v-model="search" type="text" placeholder="Search flagged processes" />
                        <span v-if="search" class="ai-search-count">{{ filteredSuggestions.length }} of {{ shownBeforeSearch.length }}</span>
                        <svg v-if="search" class="ai-search-clear" @click="search = ''" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23 20.168l-8.185-8.187 8.185-8.174-2.832-2.807-8.182 8.179-8.176-8.179-2.81 2.81 8.186 8.196-8.186 8.184 2.81 2.81 8.203-8.192 8.18 8.192z"/></svg>
                    </div>
                    <div class="fh-filter">
                        <button type="button" :class="{ on: filter == 'all' }" @click="filter = 'all'">All flagged ({{ suggestions.length }})</button>
                        <button type="button" :class="{ on: filter == 'critical' }" @click="filter = 'critical'">Critical ({{ criticalCount }})</button>
                    </div>
                </div>
                <div v-if="suggestions.length && !filteredSuggestions.length" class="ai-empty">
                    <p v-if="search">No flagged processes match "{{ search }}".</p>
                    <p v-else>No processes with critical issues.</p>
                </div>

                <div v-for="s in filteredSuggestions" :key="s.ID" class="ai-card" :class="{ 'ai-card-open': expanded == s.ID }">
                    <div class="ai-card-top" @click="toggleExpand(s.ID)">
                        <div class="ai-score" :class="scoreClass(s.Score)">{{ s.Score }}</div>
                        <div class="fh-card-main">
                            <div class="ai-card-name">
                                {{ s.NodeName }}
                                <span v-if="s.NeedsApproval" class="ai-draft-flag">draft pending</span>
                            </div>
                            <div class="fh-card-meta">
                                <span v-if="s.ProcessOwnerNames" class="fh-owner">{{ s.ProcessOwnerNames }}</span>
                                <span v-for="c in sevCounts(s)" :key="c.sev" class="fh-count" :class="'fh-count-' + c.sev">{{ c.n }} {{ c.label.toLowerCase() }}</span>
                            </div>
                        </div>
                        <div v-if="s.ImprovedScore && s.ImprovedScore > s.Score" class="fh-lift" :title="'About ' + s.ImprovedScore + ' after the fix'"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7M8 7h9v9"/></svg>+{{ s.ImprovedScore - s.Score }}</div>
                        <div class="ai-expand-arrow" :class="expanded == s.ID ? 'ai-expanded' : ''">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M0 7.33l2.829-2.83 9.175 9.339 9.167-9.339 2.829 2.83-11.996 12.17z"/></svg>
                        </div>
                    </div>

                    <div v-if="expanded == s.ID" class="fh-detail">
                        <p class="fh-summary">{{ s.Summary }}</p>
                        <div class="fh-issues">
                            <template v-for="(issue, i) in parseIssues(s.IssuesJSON)" :key="i">
                                <span class="fh-sev" :class="'fh-sev-' + issue.severity">{{ sevLabel(issue.severity) }}</span>
                                <span class="fh-issue" :title="issue.recommendation">{{ issue.issue }}<span v-if="issue.recommendation" class="fh-rec">{{ issue.recommendation }}</span></span>
                            </template>
                        </div>
                        <div class="fh-detail-foot">
                            <span v-if="s.ImprovedScore && s.ImprovedScore > s.Score" class="fh-foot-note">Fixing all of this takes it to about {{ s.ImprovedScore }}.</span>
                            <span style="flex:1"></span>
                            <a class="fh-dismiss" @click.prevent="dismiss(s)">Dismiss</a>
                            <router-link class="ai-btn ai-btn-ghost" :to="'/playbook/' + s.Slug" target="_blank">Open process</router-link>
                            <div v-if="s.DraftProcessJSON" class="ai-btn" @click="fixing = fixing == s.ID ? null : s.ID">
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
                                {{ fixing == s.ID ? 'Hide the fix' : 'Fix in the Forge' }}
                            </div>
                        </div>
                        <div v-if="fixing == s.ID" class="fh-fix">
                            <forge-compare :current="parseItems(s.CurrentProcessJSON)" :draft="parseItems(s.DraftProcessJSON)" :changes="parseJSON(s.ChangesJSON)">
                                <template #draft-label>
                                    <span v-if="s.ImprovedScore" class="ai-detail-lift">{{ s.Score }} &rarr; {{ s.ImprovedScore }}</span>
                                    <span v-if="s.SuggestedName && s.SuggestedName != s.NodeName">— "{{ s.SuggestedName }}"</span>
                                </template>
                            </forge-compare>
                            <div class="ai-card-actions">
                                <div style="flex:1"></div>
                                <div class="ai-btn" :class="applying == s.ID ? 'disable-button' : ''" @click="apply(s)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                                    {{ applying == s.ID ? 'Saving...' : 'Save as Draft for Approval' }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>

            <p class="fx-layout"><a href="#" @click.prevent="$emit('layout', 'classic')">Prefer the old layout? Switch back</a></p>
        </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AICreateNode from '@/components/AICreateNode.vue';
import ForgeTeam from '@/components/ForgeTeam.vue';
import CompanyHealth from '@/components/CompanyHealth.vue';
import ForgeCompare from '@/components/ForgeCompare.vue';
import ForgeSuggestionDraft from '@/components/ForgeSuggestionDraft.vue';
import ForgeNotetaker from '@/components/ForgeNotetaker.vue';

// The Forge page after the November 2026 cleanup (views/AIHub.vue picks this or ForgeHubClassic.vue).
// Make: one start box + one menu; a picked tool replaces the menu ("All tools" goes back). The "improve" tools live
// inside Scorecard and Rocks (ForgeTeam single mode). My captures and Health (Company + Processes) are tabs.
const TEAM_MENU = [ 'scorecard', 'rocks', 'training', 'skills' ];
const TELL_MENU = [ 'announcement', 'event' ];
// Front-screen cards: one line on what each tool makes, and its icon (24px stroke paths)
const TOOL_BLURBS: { [id: string]: string } = {
    process: 'Step by step, how a job gets done', checklist: 'A checklist for work that repeats',
    sheet: 'What a role owns and is measured on', timeline: 'The path from first call to five-star review',
    scorecard: 'Weekly numbers, new or sharpened', rocks: 'Quarter goals, milestones, or sharpen yours',
    training: 'A lesson the team can work through', skills: 'Who can do what, and how well',
    announcement: 'News for the whole team', event: 'A get-together, with the details',
    record: 'Show it once and the Forge writes it up', upload: 'Turn a document or video into a draft'
};
const TOOL_ICONS: { [id: string]: string } = {
    process: 'M5 4h5v5H5zM14 15h5v5h-5zM7.5 9v3.5a2 2 0 0 0 2 2H14',
    checklist: 'M10 6h10M10 12h10M10 18h10M4 6l1.2 1.2L7.5 5M4 12l1.2 1.2L7.5 11M4 18l1.2 1.2L7.5 17',
    sheet: 'M4 4h16v16H4zM9.5 10a2 2 0 1 0 4 0a2 2 0 1 0-4 0M7.5 17a4 4 0 0 1 8 0',
    timeline: 'M3 12h4l3-7 4 14 3-7h4',
    scorecard: 'M5 20V11M11 20V5M17 20v-6M3 20h18',
    rocks: 'M3 20l6-11 4 6 3-4 5 9z',
    training: 'M2 9l10-5 10 5-10 5zM6 11.5v4.5c3.5 2.5 8.5 2.5 12 0v-4.5',
    skills: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
    announcement: 'M3 10v4h3l7 4V6l-7 4zM17 9a4 4 0 0 1 0 6',
    event: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
    record: 'M3 4h18v12H3zM8 20h8M12 16v4M10 10a2 2 0 1 0 4 0a2 2 0 1 0-4 0',
    upload: 'M12 15V4M7 9l5-5 5 5M4 15v5h16v-5'
};
// One-tap ideas under the start box; each shows only when this person has that tool
const START_EXAMPLES = [ { text: 'A Powerlist for closing the shop', tool: 'checklist' }, { text: 'A team BBQ on Friday', tool: 'event' }, { text: 'Role sheet for a dispatcher', tool: 'sheet' } ];

export default defineComponent({
    name: 'ForgeHub',
    emits: [ 'layout' ],
    components: { AiCreateNode: AICreateNode, ForgeTeam, CompanyHealth, ForgeCompare, ForgeSuggestionDraft, ForgeNotetaker },
    data() {
        return {
            tab: 'make',       // 'make' | 'captures' | 'health' (?tab=captures / ?tab=health; old ?tab=company and #captures still work)
            tool: '',          // the open tool's key ('' = the menu); toolGroup says which card hosts it
            toolGroup: '' as string, // 'playbook' (AICreateNode) | 'team' (ForgeTeam)
            createMethod: '',  // playbook tools opened from Record: 'record' | 'upload'
            closingTool: false,
            healthView: 'processes', // 'processes' | 'company'
            companyMounted: false, companySummary: null as any,
            capturesLoaded: false,
            createKey: 0,      // remounts the inline create card for a fresh start
            filter: 'all',     // health list: 'all' | 'critical'
            fixing: null as any, // suggestion whose improved version is open
            suggestions: [] as any[],
            search: '',
            run: null as any,
            expanded: null as any,
            applying: null as any,
            loaded: false,
            pollTimer: null as any,
            isTrainer: false,
            trainingRules: [] as string[],
            confirmData: null as any,
            usage: null as any,
            capturesTimer: null as any,
            capturesGone: false,
            videoJobs: [] as any[],
            myVoice: null as any, voiceBusy: false, voiceMessage: '',
            competitiveAllowed: false,
            usageAllowed: false,
            startText: '', routing: false, startError: '', startNote: '',
            createType: 'process', createNote: '',
            playbookTools: [ { key: 'process', label: 'Process', what: 'a step-by-step how-to' }, { key: 'checklist', label: 'Powerlist', what: 'a checklist someone runs' },
                { key: 'sheet', label: 'Role sheet', what: 'seats and what each is accountable for' }, { key: 'timeline', label: 'Value engine', what: 'how work flows through the company' } ],
            teamTools: [] as any[],
            teamSuggestions: [] as any[], teamError: '', teamOpen: null as any, teamShow: 10
        }
    },
    computed: {
        isTeamHubAdmin(): boolean { return this.$store.state.user?.teamHubRole == 'Admin'; },
        canRecord(): boolean { return !!(navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia); },
        // The menu: each tool once. Improve Scorecard / Milestones / Improve Rocks open from inside Scorecard and Rocks.
        menuGroups(): any[] {
            const team = (keys: string[]) => keys.map(k => this.teamTools.find((t: any) => t.key == k)).filter(Boolean).map((t: any) => ({ id: t.key, key: t.key, label: t.label, group: 'team' }));
            const groups = [
                { label: 'Playbook', tools: this.playbookTools.map((t: any) => ({ id: t.key, key: t.key, label: t.label, group: 'playbook' })) },
                { label: 'Team', tools: team(TEAM_MENU) },
                { label: 'Tell people', tools: team(TELL_MENU) },
                { label: 'Record', tools: [
                    ...(this.canRecord ? [ { id: 'record', key: 'process', label: 'Record your screen', group: 'playbook', method: 'record' } ] : []),
                    { id: 'upload', key: 'process', label: 'Upload a file', group: 'playbook', method: 'upload' } ] }
            ];
            return groups.filter(g => g.tools.length);
        },
        examples(): any[] {
            const have = (k: string) => this.playbookTools.some((t: any) => t.key == k) || this.teamTools.some((t: any) => t.key == k);
            return START_EXAMPLES.filter(e => have(e.tool));
        },
        // Captures that need this person: ready to review, or a recording sent to them to build
        needsYou(): number {
            return this.videoJobs.filter((j: any) => j.status == 'complete' || (j.status == 'handoff' && j.handoffToMe && !j.stage)).length;
        },
        // Team suggestions, same page + near-identical words together (API GroupKey), newest group first
        teamGroups(): any[] {
            const groups: any[] = [];
            for (const r of this.teamSuggestions) {
                const g = groups.find((x: any) => x.key == r.GroupKey);
                if (g) g.rows.push(r); else groups.push({ key: r.GroupKey, first: r, rows: [ r ] });
            }
            return groups;
        },
        shownTeamGroups(): any[] { return this.teamGroups.slice(0, this.teamShow); },
        shownBeforeSearch(): any[] {
            if (this.filter != 'critical') return this.suggestions;
            return this.suggestions.filter((s: any) => this.parseIssues(s.IssuesJSON).some((i: any) => i.severity == 'high'));
        },
        criticalCount(): number {
            return this.suggestions.filter((s: any) => this.parseIssues(s.IssuesJSON).some((i: any) => i.severity == 'high')).length;
        },
        lastChecked(): string {
            const when = this.run && (this.run.Finished || this.run.Started);
            if (!when) return '';
            const d = new Date(when);
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        },
        filteredSuggestions(): any[] {
            const q = this.search.trim().toLowerCase();
            if (!q) return this.shownBeforeSearch;
            return this.shownBeforeSearch.filter((s: any) =>
                (s.NodeName || '').toLowerCase().includes(q) ||
                (s.SuggestedName || '').toLowerCase().includes(q) ||
                (s.Slug || '').toLowerCase().includes(q) ||
                (s.Summary || '').toLowerCase().includes(q)
            );
        },
        running(): boolean {
            return !!this.run && this.run.Status == 'running';
        },
        health(): any {
            return this.run?.health || null;
        },
        progressPercent(): number {
            if (!this.run || !this.run.Total) return 0;
            return Math.round((this.run.Completed / this.run.Total) * 100);
        }
    },
    async created() {
        const u = this.$store.state.user;
        if(!u.isAdmin || ![ 'Admin', 'Editor' ].includes(u.role)) {
            this.$router.replace('/');
            return;
        }
        this.followRoute();
        // The team tools this person can use, for the menu (ForgeTeam works out the permissions)
        this.$nextTick(() => {
            this.teamTools = (((this.$refs.forgeTeam as any)?.actions) || []).map((a: any) => ({ key: a.key, label: a.label, sub: a.sub }));
            // /ai-hub?tool=rocks opens a tool straight away (links from other pages)
            const t = String(this.$route.query.tool || '');
            if (this.playbookTools.some((x: any) => x.key == t)) this.openPlaybook(t);
            else if (this.teamTools.some((x: any) => x.key == t)) this.openTeam(t);
        });
        await this.refresh();
        axios.get(`${this.$store.state.apiUrl}/api/competitive/access`)
            .then(r => { this.competitiveAllowed = r.data?.allowed === true; })
            .catch(() => { /* link only */ });
        axios.get(`${this.$store.state.apiUrl}/api/usage/access`)
            .then(r => { this.usageAllowed = r.data?.allowed === true; })
            .catch(() => { /* link only */ });
        axios.get(`${this.$store.state.apiUrl}/api/ai/training`)
            .then(async resp => {
                this.isTrainer = resp.data.isTrainer;
                this.trainingRules = resp.data.rules;
                if (this.isTrainer) {
                    await axios.get(`${this.$store.state.apiUrl}/api/ai/usage/summary`)
                        .then(r => { this.usage = r.data; })
                        .catch(() => { /* indicator only */ });
                }
            })
            .catch(e => console.log(e));
        this.$store.state.routeLoaded = true;
        await this.loadCaptures();
        axios.get(`${this.$store.state.apiUrl}/api/ai/voice-profile`).then(r => { this.myVoice = r.data; }).catch(() => { /* not offered */ });
        if (this.running) this.startPolling();
    },
    watch: {
        // A link to /ai-hub#captures or ?tab=… while already on this page
        '$route.fullPath'() { this.followRoute(); }
    },
    unmounted() {
        this.capturesGone = true;
        if (this.capturesTimer) clearTimeout(this.capturesTimer);
        this.stopPolling();
    },
    methods: {
        // What a capture job built, for its "… created" line
        jobTypeName(nodeType: string): string {
            return ({ checklist: 'Powerlist', sheet: 'Role sheet', timeline: 'Value engine' } as { [k: string]: string })[nodeType] || 'Process';
        },
        // "What do you want to make?": the Forge picks the tool (team-draft kind route), then opens it with the text
        async routeStart() {
            const text = this.startText.trim();
            if (!text || this.routing) return;
            this.routing = true; this.startError = ''; this.startNote = '';
            const tools = [ ...this.playbookTools.map((t: any) => ({ key: t.key, label: t.label, what: t.what })), ...this.teamTools.map((t: any) => ({ key: t.key, label: t.label, what: t.sub })) ];
            try {
                const r = (await axios.post(`${this.$store.state.apiUrl}/api/ai/team-draft`, { kind: 'route', description: text, context: { tools } })).data;
                const tool = tools.find((t: any) => t.key == r.kind);
                if (!tool) throw new Error('no tool');
                if (this.playbookTools.some((t: any) => t.key == tool.key)) this.openPlaybook(tool.key, text);
                else await this.openTeam(tool.key, text);
                this.startNote = `Opened ${tool.label}: ${r.reason}`;
                this.startText = '';
                this.$nextTick(() => this.growStart());
            } catch (e: any) {
                const d = e?.response?.data;
                this.startError = typeof d == 'string' && d && d.length < 300 ? d : 'The Forge couldn\'t tell which tool fits. Pick one from the menu below.';
            }
            this.routing = false;
        },
        toolIcon(id: string): string { return TOOL_ICONS[id] || TOOL_ICONS.process; },
        toolBlurb(t: any): string { return TOOL_BLURBS[t.id] || (this.teamTools.find((x: any) => x.key == t.key) || {}).sub || ''; },
        // An idea under the start box: fill it in and start straight away
        tryExample(text: string) {
            this.startText = text;
            this.$nextTick(() => this.growStart());
            this.routeStart();
        },
        // The start box is one line until the words need more (up to about five lines)
        growStart() {
            const el = this.$refs.startBox as HTMLTextAreaElement | undefined;
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 140) + 'px';
        },
        openTool(t: any) {
            if (t.group == 'playbook') this.openPlaybook(t.key, '', t.method || '');
            else this.openTeam(t.key);
        },
        openPlaybook(key: string, note = '', method = '') {
            this.startNote = '';
            this.tool = key;
            this.toolGroup = 'playbook';
            this.createType = key;
            this.createNote = note;
            this.createMethod = method;
            this.createKey++;
            this.showTool();
        },
        async openTeam(key: string, note = '') {
            const ft: any = this.$refs.forgeTeam;
            if (!ft || ft.busy) return;
            this.startNote = '';
            this.tool = key;
            this.toolGroup = 'team';
            await ft.pick(key);
            if (note) ft.description = note;
            this.showTool();
        },
        showTool() {
            this.$nextTick(() => (this.$refs.toolBar as HTMLElement)?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
        },
        // "All tools": back to the menu. A draft that isn't saved yet, or a recording, asks first (the tool's own check).
        async backToMenu() {
            if (this.toolGroup == 'playbook') {
                const cn: any = this.$refs.createNode;
                this.closingTool = true;
                try { if (cn?.requestClose) await cn.requestClose(); else this.closeTool(); }
                finally { this.closingTool = false; }
                return;
            }
            const ft: any = this.$refs.forgeTeam;
            if (ft) {
                if (ft.busy) return;
                if (ft.phase == 'review' && !confirm('Leave this draft? Nothing from it has been added yet.')) return;
                ft.startOver(true);
                ft.kind = '';
            }
            this.closeTool();
        },
        closeTool() {
            this.tool = '';
            this.toolGroup = '';
            this.startNote = '';
            this.createMethod = '';
        },
        // The create card's own close (× = start over, or our All tools)
        onCreateClose() {
            if (this.closingTool) this.closeTool();
            else this.createKey++;
        },
        setTab(tab: string) {
            this.tab = tab;
            if (tab == 'health' && this.isTeamHubAdmin) this.companyMounted = true;
            const query: any = { ...this.$route.query };
            if (tab == 'make') delete query.tab;
            else query.tab = tab == 'health' && this.healthView == 'company' ? 'company' : tab;
            if (JSON.stringify(query) != JSON.stringify(this.$route.query) || this.$route.hash) this.$router.replace({ query, hash: '' });
        },
        setHealthView(view: string) {
            this.healthView = view;
            this.setTab('health');
        },
        // Tab from the address: ?tab=captures|health|company, #captures (Meetings, meeting review, Daily Digest),
        // #existing-processes (old health link)
        followRoute() {
            const q = String(this.$route.query.tab || ''), h = this.$route.hash;
            if (h === '#captures' || q == 'captures') this.tab = 'captures';
            else if (q == 'company' && this.isTeamHubAdmin) { this.tab = 'health'; this.healthView = 'company'; }
            else if (q == 'health' || q == 'company' || h === '#existing-processes') { this.tab = 'health'; this.healthView = 'processes'; }
            if (this.tab == 'health' && this.isTeamHubAdmin) this.companyMounted = true;
        },
        sevLabel(sev: string): string {
            return sev == 'high' ? 'Critical' : sev == 'medium' ? 'Important' : 'Minor';
        },
        sevCounts(s: any): any[] {
            const issues = this.parseIssues(s.IssuesJSON);
            return ['high', 'medium', 'low']
                .map(sev => ({ sev, label: this.sevLabel(sev), n: issues.filter((i: any) => i.severity == sev).length }))
                .filter(c => c.n);
        },
        ringDash(score: number): string {
            const c = 2 * Math.PI * 31;
            return `${(Math.max(0, Math.min(100, score)) / 100) * c} ${c}`;
        },
        async forgetVoice() {
            if (!confirm('Forget your voice? Future recordings will no longer name you automatically.')) return;
            this.voiceBusy = true; this.voiceMessage = '';
            try { this.myVoice = (await axios.delete(`${this.$store.state.apiUrl}/api/ai/voice-profile`)).data; this.voiceMessage = 'Your saved voice was deleted.'; }
            catch { this.voiceMessage = 'Could not delete your saved voice. Please try again.'; }
            finally { this.voiceBusy = false; }
        },
        async loadCaptures() {
            if (this.capturesTimer) clearTimeout(this.capturesTimer);
            try { this.videoJobs = (await axios.get(`${this.$store.state.apiUrl}/api/ai/video-jobs`)).data; }
            catch { /* Keep the last successful list during a temporary connection failure. */ }
            this.capturesLoaded = true;
            if (!this.capturesGone && this.videoJobs.some((j: any) => ['recording', 'processing'].includes(j.status) || (j.status == 'handoff' && j.stage))) this.capturesTimer = setTimeout(() => this.loadCaptures(), 5000);
        },
        async refresh() {
            await Promise.all([
                axios.get(`${this.$store.state.apiUrl}/api/ai/suggestions`)
                    .then(resp => { this.suggestions = resp.data; }),
                axios.get(`${this.$store.state.apiUrl}/api/ai/runs/latest`)
                    .then(resp => { this.run = resp.data; })
            ]).catch(e => console.log(e));
            this.loaded = true;
            await this.loadTeamSuggestions();
        },
        async loadTeamSuggestions() {
            try {
                this.teamSuggestions = (await axios.get(`${this.$store.state.apiUrl}/api/ai/team-suggestions`)).data || [];
                this.teamError = '';
            } catch (e: any) {
                const d = e?.response?.data;
                this.teamError = e?.response?.status == 503 && typeof d == 'string' ? d : '';
            }
        },
        shortDate(d: string): string {
            return d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        },
        openTeamSuggestion(g: any) { this.teamOpen = g; },
        markTeam(id: string, status: string) {
            const r = this.teamSuggestions.find((x: any) => x.ID == id);
            if (r) { r.ForgeStatus = status; r.HasDraft = true; }
        },
        // Saved as a draft: the same suggestion from others is handled too
        async teamSaved(e: any) {
            const g = this.teamOpen;
            this.teamOpen = null;
            const others = g ? g.rows.filter((r: any) => r.ID != e.id) : [];
            for (const r of others) await axios.post(`${this.$store.state.apiUrl}/api/ai/team-suggestions/${r.ID}/dismiss`, {}).catch(() => null);
            this.teamSuggestions = this.teamSuggestions.filter((x: any) => x.ID != e.id && !others.some((o: any) => o.ID == x.ID));
            alert('Saved as a draft. Review and approve it under Draft / Needs Approval.');
        },
        async dismissTeamGroup(g: any) {
            if (!g) return;
            for (const r of g.rows) await axios.post(`${this.$store.state.apiUrl}/api/ai/team-suggestions/${r.ID}/dismiss`, {}).catch(() => null);
            await this.loadTeamSuggestions();
        },
        async askConfirm() {
            if (this.running) return;
            await axios.get(`${this.$store.state.apiUrl}/api/ai/analyze/preview`)
                .then(resp => {
                    this.confirmData = resp.data;
                })
                .catch(e => {
                    alert(e?.response?.data || 'Could not check the number of processes');
                });
        },
        async startAnalysis() {
            if (this.running) return;
            this.confirmData = null;
            await axios.post(`${this.$store.state.apiUrl}/api/ai/analyze`, {})
                .then(resp => {
                    // keep showing the last score until the new check reports
                    this.run = { Status: 'running', Completed: 0, Total: resp.data.total, health: this.run?.health || null };
                    this.startPolling();
                })
                .catch(e => {
                    alert(e?.response?.data || 'Could not start the analysis');
                });
        },
        healthClass(score: number): string {
            if (score >= 85) return 'great';
            if (score >= 70) return 'good';
            if (score >= 50) return 'fair';
            return 'poor';
        },
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(async () => {
                await axios.get(`${this.$store.state.apiUrl}/api/ai/runs/latest`)
                    .then(async resp => {
                        this.run = resp.data;
                        if (!this.running) {
                            this.stopPolling();
                            await this.refresh();
                        } else {
                            // refresh suggestions as they stream in
                            await axios.get(`${this.$store.state.apiUrl}/api/ai/suggestions`)
                                .then(r => { this.suggestions = r.data; });
                        }
                    })
                    .catch(e => console.log(e));
            }, 2500);
        },
        stopPolling() {
            if (this.pollTimer) clearInterval(this.pollTimer);
            this.pollTimer = null;
        },
        async apply(s: any, force = false) {
            if (this.applying) return;
            if (!force && (s.DraftProcessJSON || '').includes('[confirm:')) {
                if (!confirm('This improved version contains [confirm: ...] placeholders the process owner should fill in before it goes live. Save as draft anyway?')) return;
            }
            this.applying = s.ID;
            await axios.post(`${this.$store.state.apiUrl}/api/ai/suggestions/${s.ID}/apply`, { force })
                .then(() => {
                    alert(`Saved as a draft. Review and approve it under Draft / Needs Approval.`);
                    this.suggestions = this.suggestions.filter(x => x.ID != s.ID);
                })
                .catch(async e => {
                    if (e?.response?.status == 409) {
                        this.applying = null;
                        if (confirm(e.response.data + ' Apply anyway and overwrite the newer edits?')) {
                            await this.apply(s, true);
                        }
                        return;
                    }
                    alert(e?.response?.data || 'Could not apply the suggestion');
                });
            this.applying = null;
        },
        async dismiss(s: any) {
            await axios.post(`${this.$store.state.apiUrl}/api/ai/suggestions/${s.ID}/dismiss`, {})
                .then(() => {
                    this.suggestions = this.suggestions.filter(x => x.ID != s.ID);
                })
                .catch(e => console.log(e));
        },
        toggleExpand(id: any) {
            this.expanded = this.expanded == id ? null : id;
        },
        scoreClass(score: number): string {
            if (score >= 85) return 'ai-score-great';
            if (score >= 70) return 'ai-score-good';
            if (score >= 50) return 'ai-score-fair';
            return 'ai-score-poor';
        },
        parseIssues(json: string): any[] {
            return this.parseJSON(json);
        },
        parseItems(json: string): any[] {
            try {
                return JSON.parse(json || '{"items":[]}').items || [];
            } catch (e) { return []; }
        },
        parseJSON(json: string): any[] {
            try {
                const v = JSON.parse(json || '[]');
                return Array.isArray(v) ? v : [];
            } catch (e) { return []; }
        }
    }
});
</script>

<style scoped>
.fh-notetaker { margin-bottom: 18px; }
/* Front screen (mockup C, "Forge band"). The band bleeds past .view's side padding so it runs under the tabs edge to edge. */
.fb-band {
    margin: -15px -30px 0; padding: 52px 32px 60px; border-top: 1px solid #3a3735; text-align: center;
    background: radial-gradient(ellipse 60% 120% at 50% 118%, rgba(249, 115, 22, .42) 0%, rgba(234, 88, 12, .16) 38%, rgba(44, 42, 41, 0) 70%), #2c2a29;
}
.fb-inner { max-width: 820px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; gap: 18px; }
.fb-flame { width: 34px; height: 34px; fill: #f97316; }
.fb-band h2 { margin: 0; font-size: 36px; line-height: 1.15; font-weight: 800; letter-spacing: -.5px; color: #fff; text-wrap: balance; }
.fb-band p { margin: -4px 0 4px; font-size: 16.5px; line-height: 1.5; color: #cfcac4; }
.fb-bar {
    width: 100%; box-sizing: border-box; display: flex; align-items: center; gap: 10px; background: #fff; border-radius: 14px; padding: 8px 8px 8px 20px;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, .08), 0 18px 48px -12px rgba(0, 0, 0, .55), 0 0 0 6px rgba(249, 115, 22, .14);
}
.fb-bar:focus-within { box-shadow: 0 0 0 1px rgba(255, 255, 255, .08), 0 18px 48px -12px rgba(0, 0, 0, .55), 0 0 0 6px rgba(249, 115, 22, .38); }
.fb-bar textarea {
    flex: 1; min-width: 0; margin: 0; border: 0; outline: none; resize: none; background: transparent; box-shadow: none;
    font: inherit; font-size: 18px; line-height: 26px; color: #2c2a29; padding: 11px 0; height: 48px; max-height: 140px; text-align: left;
}
.fb-bar textarea::placeholder { color: #8a8580; }
.fb-go {
    flex-shrink: 0; align-self: flex-end; height: 48px; padding: 0 24px; border: 0; border-radius: 10px; background: var(--ai-grad); color: #fff;
    font-family: var(--head-font); font-weight: 700; font-size: 15px; cursor: pointer; white-space: nowrap;
}
.fb-go:hover:not(:disabled) { background: var(--ai-grad-hover); }
.fb-go:disabled { opacity: .55; cursor: default; }
.fb-error { color: #fecaca; font-size: 14px; max-width: 640px; }
.fb-examples { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
.fb-ex {
    background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .14); color: #efebe6; border-radius: 999px; padding: 7px 13px;
    font: inherit; font-size: 13.5px; font-weight: 500; cursor: pointer;
}
.fb-ex:hover:not(:disabled) { background: rgba(249, 115, 22, .18); border-color: rgba(253, 186, 140, .5); }
.fb-ex:disabled { opacity: .55; cursor: default; }
.fb-go:focus-visible, .fb-ex:focus-visible, .fb-tile:focus-visible, .fb-waiting:focus-visible { outline: 3px solid #fdba8c; outline-offset: 2px; }
.ai-hub.ai-hub-wide { max-width: 1120px; padding-top: 32px; }
.fb-waiting {
    display: flex; align-items: center; gap: 12px; text-decoration: none; background: #fff; border: 1px solid #fed7aa; border-radius: 12px;
    padding: 12px 16px; margin: 0 0 28px; font-size: 14.5px; font-weight: 500; color: #2c2a29;
}
.fb-waiting:hover { border-color: #fdba74; text-decoration: none; color: #2c2a29; }
.fb-count { background: var(--ai-accent); color: #fff; font-family: var(--head-font); font-weight: 700; font-size: 12px; border-radius: 999px; padding: 2px 8px; }
.fb-waiting-text { flex: 1; min-width: 0; }
.fb-waiting-go { font-weight: 600; color: var(--ai-accent-deep); white-space: nowrap; }
.fb-groups { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; align-items: start; }
.fb-group { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.fb-group h3 { margin: 0 0 0 2px; font-size: 15px; font-weight: 700; color: #2c2a29; }
.fb-tile {
    display: flex; flex-direction: column; align-items: flex-start; gap: 10px; width: 100%; box-sizing: border-box; text-align: left;
    background: #fff; border: 1px solid #e6e1db; border-radius: 12px; padding: 16px; cursor: pointer; font: inherit; color: #2c2a29;
    transition: border-color .15s, box-shadow .15s;
}
.fb-tile:hover { border-color: #fdba8c; box-shadow: 0 8px 20px -10px rgba(234, 88, 12, .35); }
.fb-ic { width: 36px; height: 36px; border-radius: 9px; background: #fff1e6; color: #c2410c; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fb-ic svg { width: 19px; height: 19px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.fb-tn { font-size: 15px; font-weight: 600; }
.fb-td { font-size: 13.5px; line-height: 1.4; color: #646464; margin-top: -4px; }
.fb-tile-dark { background: #2c2a29; border-color: #2c2a29; color: #fff; }
.fb-tile-dark .fb-td { color: #cfcac4; }
.fb-tile-dark .fb-ic { background: rgba(249, 115, 22, .18); color: #fdba74; }
@media (max-width: 1100px) { .fb-groups { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 767px) {
    .fb-band { margin: -15px -10px 0; padding: 30px 16px 34px; }
    .fb-inner { gap: 14px; }
    .fb-flame { width: 28px; height: 28px; }
    .fb-band h2 { font-size: 26px; }
    .fb-band p { font-size: 15px; margin: -4px 0 2px; }
    .fb-bar { padding: 6px 6px 6px 14px; border-radius: 12px; }
    .fb-bar textarea { font-size: 16px; line-height: 22px; padding: 10px 0; height: 44px; }
    /* The example text stays on one line (ends in "…") instead of wrapping under the box's edge */
    .fb-bar textarea::placeholder { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .fb-go { height: 44px; padding: 0 16px; font-size: 14.5px; }
    .ai-hub.ai-hub-wide { padding-top: 20px; }
    .fb-waiting { flex-wrap: wrap; margin-bottom: 22px; font-size: 14px; }
    .fb-waiting-text { flex-basis: calc(100% - 50px); }
    .fb-waiting-go { margin-left: 38px; }
    /* Phones: one column, and each card becomes a compact row (icon left, words right) so 12 tools don't need a long scroll */
    .fb-groups { grid-template-columns: 1fr; gap: 18px; }
    .fb-group { gap: 8px; }
    .fb-tile { display: grid; grid-template-columns: 36px 1fr; column-gap: 12px; row-gap: 2px; align-items: center; padding: 12px 14px; }
    .fb-ic { grid-row: 1 / span 2; }
    .fb-td { margin-top: 0; }
}
.fs-note { color: #2c7a4b; font-size: 14px; }
.fs-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin: 0 0 12px; scroll-margin-top: 120px; }
.fs-back { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #ece3da; border-radius: 999px; padding: 6px 14px; font: inherit; font-size: 14px; font-weight: 600; color: #3a2f27; cursor: pointer; }
.fs-back:hover { border-color: #f2a36b; background: #fff4ea; }
.fs-toolbar .fs-note { flex: 1; min-width: 200px; }
/* The tool card sits right under the toolbar (both cards carry their own top margin elsewhere) */
.ai-hub :deep(.ft-host .ft) { margin-top: 0; }
.fx-layout { margin: 34px 0 0; text-align: center; font-size: 13px; }
.fx-layout a { color: #8a7f75; text-decoration: underline; }

/* Health: Company and Processes as two tiles that pick what shows below */
.fh-tiles { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.fh-tile { display: flex; align-items: center; gap: 14px; text-align: left; background: #fff; border: 1px solid #f1e4d8; border-radius: 14px; padding: 12px 16px; font: inherit; cursor: pointer; box-shadow: 0 1px 6px rgba(124, 58, 18, .06); }
.fh-tile:hover { border-color: #f6c9a4; }
.fh-tile-on { border-color: var(--ai-accent); box-shadow: 0 0 0 2px rgba(234, 88, 12, .14), 0 4px 18px rgba(124, 58, 18, .10); }
.fh-mini { position: relative; width: 52px; height: 52px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.fh-mini svg { position: absolute; inset: 0; transform: rotate(-90deg); }
.fh-mini b { font-size: 17px; font-weight: 800; color: #1f1712; }
.fh-mini .fh-ring-track, .fh-mini .fh-ring-fill { stroke-width: 8; }
.fh-mini-none { border: 6px solid #f3ece5; border-radius: 50%; box-sizing: border-box; }
.fh-mini-none b { color: #b3a89c; }
.fh-tile-text { display: grid; gap: 2px; min-width: 0; }
.fh-tile-text strong { font-size: 15.5px; color: #1f1712; }
.fh-tile-text em { font-style: normal; font-size: 12.5px; color: #8a7f75; }
.fh-card-slim { padding: 14px 20px; gap: 14px; }
.fh-card-slim .fh-meta { min-width: 160px; }
.ai-empty.ai-empty-short { padding: 18px 0 8px; }
@media (max-width: 520px) {
    .fh-tiles { gap: 8px; }
    .fh-tile { padding: 10px 12px; gap: 10px; }
    .fh-mini { width: 42px; height: 42px; } .fh-mini b { font-size: 15px; } .fh-mini-none { border-width: 5px; }
    .fh-tile-text strong { font-size: 14.5px; }
}
@media (max-width: 340px) { .fh-tiles { grid-template-columns: 1fr; } }


.ai-hub { max-width: 1000px; margin: 0 auto; padding: 22px 16px 60px; }
.ai-confirm {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    background: white; border: 1px solid var(--ai-border-soft); border-radius: 12px; padding: 10px 14px;
    box-shadow: var(--ai-shadow-card);
}
.ai-confirm-text { font-size: 14px; color: #2b221b; }
.ai-confirm-est { color: #a0958a; font-size: 12.5px; margin-left: 4px; }
.ai-detail-lift {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0); color: #15803d; border-radius: 10px;
    padding: 1px 8px; font-weight: 800; margin: 0 4px;
}
.ai-confirm-flag { background: #fff3cd; border: 1px solid #ffe08a; color: #8a6d00; border-radius: 4px; padding: 0 4px; font-size: 12px; }
.ai-progress { margin: 20px 0; }
.ai-progress-label { font-weight: 600; margin-bottom: 8px; color: #4a3728; }
.ai-progress-track { height: 10px; background: #fdefe2; border-radius: 6px; overflow: hidden; }
.ai-progress-bar { height: 100%; background: var(--ai-grad); border-radius: 6px; transition: width .6s ease; }
.ai-run-error { background: #fdecea; border: 1px solid #f5c6c3; color: #9c2b23; padding: 10px 14px; border-radius: 10px; margin: 12px 0; }
.ai-empty { text-align: center; color: #a0958a; padding: 60px 0; }
.ai-search {
    display: flex; align-items: center; gap: 10px;
    margin: 18px 0 14px; padding: 10px 16px;
    background: #fffcf9; border: 1px solid #f6e3d3; border-radius: 12px;
    box-shadow: inset 0 2px 6px rgba(124, 58, 18, .04);
    color: #8a7f75;
}
.ai-search:focus-within { border-color: #fdba74; box-shadow: 0 0 0 3px rgba(249, 115, 22, .12); }
.ai-search input {
    flex: 1; border: none; outline: none; background: transparent;
    font-size: 14.5px; color: #2b221b; padding: 2px 0; margin: 0;
}
.ai-search input::placeholder { color: #b3a89c; }
.ai-search-count { font-size: 12.5px; color: #8a7f75; white-space: nowrap; }
.ai-search-clear { cursor: pointer; flex-shrink: 0; }
.ai-search-clear:hover { color: #2b221b; }
.ai-card-actions { display: flex; gap: 10px; margin-top: 16px; align-items: center; }
.ft-team { background: #fff; border: 1px solid #f6e3d3; border-radius: 14px; padding: 14px 18px; margin: 0 0 18px; }
.ft-team-h { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.ft-team-h h3 { margin: 0; font-size: 16px; color: #1f1712; display: flex; align-items: center; gap: 8px; }
.ft-team-count { background: var(--ai-grad); color: #fff; border-radius: 999px; font-size: 12px; padding: 1px 9px; }
.ft-team-all { font-size: 13px; color: var(--ai-accent-deep); }
.ft-team-row { display: flex; gap: 14px; align-items: flex-start; padding: 12px 0; border-top: 1px solid #fbf1e8; margin-top: 10px; }
.ft-team-main { flex: 1; min-width: 0; }
.ft-team-node { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-weight: 700; font-size: 14px; }
.ft-team-dup { background: #f1eefa; color: #5b3fa3; border-radius: 10px; font-size: 11.5px; font-weight: 700; padding: 1px 8px; }
.ft-team-text { margin: 4px 0; font-size: 13.5px; color: #3a2f27; white-space: pre-wrap; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.ft-team-by { font-size: 12px; color: #8a7f75; }
.ft-team-acts { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
.ft-team-na { font-size: 12.5px; color: #8a7f75; }
.ft-team-more { display: inline-block; margin-top: 8px; font-size: 13px; cursor: pointer; color: var(--ai-accent-deep); }
@media (max-width: 640px) { .ft-team-row { flex-direction: column; } .ft-team-acts { flex-direction: row; align-items: center; } }
.ai-drafts { background: #fff; border: 1px solid #f6e3d3; border-radius: 14px; padding: 14px 18px; margin-bottom: 22px; }
.ai-voice { margin-top: 22px; font-size: 14px; color: #4b4138; }
.ai-voice p { margin: 0 0 6px; line-height: 1.55; }
.ai-voice-forget { border: 0; background: none; padding: 0 0 0 6px; color: #a64010; text-decoration: underline; cursor: pointer; font: inherit; }
.ai-voice-forget:disabled { opacity: .5; cursor: default; }
.ai-voice-note { color: #2f6b45; }
.ai-drafts-title { font-weight: 700; text-transform: uppercase; font-size: 11.5px; letter-spacing: .08em; color: #a0958a; margin-bottom: 8px; }
.ai-drafts-row { display: flex; align-items: center; gap: 12px; padding: 7px 0; border-bottom: 1px solid #fbf1e8; font-size: 13.5px; }
.ai-drafts-row:last-child { border-bottom: none; }
.ai-drafts-chip { font-size: 11.5px; font-weight: 700; border-radius: 12px; padding: 2px 10px; white-space: nowrap; }
.ai-drafts-processing { background: #fff8e6; color: #8a6d00; border: 1px solid #ffe08a; }
.ai-drafts-complete { background: #eafaf0; color: #1b7a3d; border: 1px solid #b7e4c7; }
.ai-drafts-consumed { background: #f5f2ef; color: #6f655c; border: 1px solid #e9ddd2; }
.ai-drafts-error { background: #fdecea; color: #9c2b23; border: 1px solid #f5c6c3; }
.ai-drafts-handoff { background: #eef4ff; color: #1d4ed8; border: 1px solid #c7d7fe; }
.ai-drafts-discarded { background: #f5f2ef; color: #6f655c; border: 1px solid #e9ddd2; }
.ai-drafts-name { color: #2b221b; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-drafts-sub { color: #a0958a; font-size: 12px; margin-left: 6px; }
.ai-drafts-date { color: #a0958a; font-size: 12px; white-space: nowrap; }
.ai-drafts-link { color: var(--ai-accent); text-decoration: underline; }
/* Tabs in the page bar */
.forge-tabs > div { display: inline-flex; align-items: center; gap: 7px; }
.forge-tabs > div > svg { width: 14px; height: 14px; flex-shrink: 0; }
.forge-tabs > div > span { margin-left: 2px; }
.forge-tabs > div.page-bar-toggle-s { color: white; font-weight: 600; }
.forge-tabs .ft-label, .forge-tabs .ft-label i { font-style: normal; font-weight: inherit; white-space: nowrap; }
.forge-tabs i.ft-short { display: none; }
/* Phones: short labels ("Make", "Health", "Company"), and the row scrolls sideways inside itself if it still
   doesn't fit, so it never widens the page (a wider page let phones pan sideways and hid the menu button) */
@media (max-width: 600px) {
    .page-bar-toggles-wrap { min-width: 0; }
    .forge-tabs { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; max-width: 100%; }
    .forge-tabs::-webkit-scrollbar { display: none; }
    .forge-tabs > div { padding: 3px 10px 8px; white-space: nowrap; flex-shrink: 0; }
    .forge-tabs i.ft-long { display: none; }
    .forge-tabs i.ft-short { display: inline; }
}
@media (max-width: 360px) { .forge-tabs > div { padding: 3px 6px 8px; gap: 5px; } }

/* Playbook health summary */
.fh-card {
    position: relative; display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
    background: linear-gradient(180deg, #fff8f1 0, #fff 120px); border-radius: 16px; padding: 22px 26px;
    box-shadow: 0 2px 14px rgba(124, 58, 18, .08), 0 0 0 1px #f6e3d3; overflow: hidden;
}
.fh-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--ai-grad); }
.fh-ring { position: relative; width: 84px; height: 84px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.fh-ring svg { position: absolute; inset: 0; transform: rotate(-90deg); }
.fh-ring span { font-size: 26px; font-weight: 800; color: #1f1712; }
.fh-ring-track { fill: none; stroke: #f3ece5; stroke-width: 7; }
.fh-ring-fill { fill: none; stroke-width: 7; stroke-linecap: round; transition: stroke-dasharray .6s ease; }
.fh-ring-poor .fh-ring-fill { stroke: #dc2626; }
.fh-ring-fair .fh-ring-fill { stroke: #e0a526; }
.fh-ring-good .fh-ring-fill { stroke: #65a30d; }
.fh-ring-great .fh-ring-fill { stroke: #16a34a; }
.fh-ring-none { border: 7px solid #f3ece5; border-radius: 50%; box-sizing: border-box; }
.fh-ring-none span { color: #b3a89c; }
.fh-meta { flex: 1; min-width: 200px; }
.fh-label { font-size: 12.5px; color: #8a7f75; font-weight: 600; }
.fh-score { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.fh-score strong { font-size: 30px; font-weight: 800; color: #1f1712; line-height: 1.15; }
.fh-proj { color: #16a34a; font-weight: 700; font-size: 15px; }
.fh-sub { color: #8a7f75; font-size: 13px; margin-top: 2px; }
.fh-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.fh-rules {
    display: inline-flex; align-items: center; gap: 7px; padding: 8px 14px; border-radius: 10px;
    background: #fff; border: 1px solid #ece3da; color: #2b221b; font-size: 13.5px; font-weight: 600; text-decoration: none;
}
.fh-rules:hover { border-color: #fdba74; text-decoration: none; color: #2b221b; }
.fh-rules-static { cursor: default; }
.fh-rules-static:hover { border-color: #ece3da; }
.fh-rules svg { width: 14px; height: 14px; }
.fh-rules span { font-size: 11.5px; background: #f5f0eb; border-radius: 9px; padding: 0 7px; color: #6f655c; }
.ai-usage-note { flex-basis: 100%; font-size: 12.5px; color: #8a7f75; cursor: default; }
.fh-intro { color: #6f655c; font-size: 14px; margin: 16px 2px 14px; max-width: 720px; }

/* Search + filter */
.fh-tools { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 14px; }
.fh-tools .ai-search { flex: 1; min-width: 240px; margin: 0; }
.fh-filter { display: inline-flex; gap: 2px; padding: 3px; background: #fff; border: 1px solid #ece3da; border-radius: 10px; }
.fh-filter button { border: none; background: transparent; border-radius: 8px; padding: 6px 12px; font: inherit; font-size: 13px; color: #6f655c; cursor: pointer; }
.fh-filter button.on { background: var(--primary-color); color: #fff; font-weight: 600; }

/* Flagged process cards */
.ai-card { border: 1px solid #f1e4d8; border-radius: 14px; margin-bottom: 12px; background: #fff; box-shadow: 0 1px 6px rgba(124, 58, 18, .06); }
.ai-card-open { border-color: #f6c9a4; box-shadow: 0 4px 18px rgba(124, 58, 18, .12); }
.ai-card-top { display: flex; gap: 16px; padding: 16px 20px; cursor: pointer; align-items: center; }
.ai-score { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; color: white; flex-shrink: 0; }
.ai-score-poor { background: #dc2626; }
.ai-score-fair { background: #e0a526; }
.ai-score-good { background: #65a30d; }
.ai-score-great { background: #16a34a; }
.fh-card-main { flex: 1; min-width: 0; }
.ai-card-name { font-weight: 700; font-size: 16px; color: #1f1712; }
.ai-draft-flag { font-size: 11px; background: #fff3cd; border: 1px solid #ffe08a; color: #8a6d00; border-radius: 10px; padding: 1px 8px; margin-left: 8px; vertical-align: middle; }
.fh-card-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 4px; font-size: 12.5px; color: #8a7f75; }
.fh-owner::after { content: '\00b7'; margin-left: 6px; }
.fh-count { border-radius: 10px; padding: 1px 8px; font-size: 11.5px; font-weight: 600; }
.fh-count-high { background: #fdecea; color: #b42318; }
.fh-count-medium { background: #fff4e0; color: #9a6400; }
.fh-count-low { background: #eaf2fb; color: #2c5c8f; }
.fh-lift { display: inline-flex; align-items: center; gap: 3px; color: #16a34a; font-weight: 700; font-size: 13.5px; white-space: nowrap; }
.ai-expand-arrow { color: #b3a89c; transition: .3s transform; display: flex; }
.ai-expanded { transform: rotate(180deg); }
.fh-detail { padding: 0 20px 18px 80px; }
.fh-summary { margin: 0 0 12px; color: #3d332b; font-size: 14.5px; }
.fh-issues { display: grid; grid-template-columns: max-content 1fr; column-gap: 18px; row-gap: 8px; font-size: 14px; color: #2b221b; }
.fh-sev { font-size: 11.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; padding-top: 2px; }
.fh-sev-high { color: #b42318; }
.fh-sev-medium { color: #9a6400; }
.fh-sev-low { color: #2c5c8f; }
.fh-rec { display: block; color: #8a7f75; font-size: 12.5px; margin-top: 1px; }
.fh-detail-foot { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 16px; }
.fh-foot-note { color: #8a7f75; font-size: 13px; }
.fh-dismiss { color: #8a7f75; font-size: 13px; cursor: pointer; text-decoration: underline; margin-right: 4px; }
.fh-dismiss:hover { color: #2b221b; }
.fh-detail-foot .ai-btn-ghost { text-decoration: none; }
.fh-fix { margin-top: 16px; padding-top: 16px; border-top: 1px solid #f6e9dd; }
@media (max-width: 640px) {
    .fh-detail { padding-left: 20px; }
    .fh-issues { grid-template-columns: 1fr; row-gap: 2px; }
    .fh-issues .fh-issue { margin-bottom: 8px; }
}
</style>
